import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Users, Briefcase, Wrench } from 'lucide-react';

const StaffDO = () => {
  const paths = [
    {
      icon: <Briefcase className="w-12 h-12 text-terracota" />,
      title: 'For Employers',
      desc: 'Hire with visibility and speed—without the chaos.',
      details: 'Includes: job postings, applicant intake, screening workflows, and trust-building verification.',
      cta: 'Request an Employer Demo',
    },
    {
      icon: <Users className="w-12 h-12 text-terracota" />,
      title: 'For Talent',
      desc: 'A professional profile that helps you get discovered and hired for real opportunities.',
      details: '',
      cta: 'Create a Talent Profile',
    },
    {
      icon: <Wrench className="w-12 h-12 text-terracota" />,
      title: 'For Contractors / Professional Services',
      desc: 'A dedicated module for independent professionals and service providers offering project-based or specialized services.',
      details: '',
      cta: 'Create a Contractor Profile',
    },
  ];

  const solves = [
    'Faster hiring decisions',
    'Stronger trust signals',
    'Cleaner workflows from application to selection',
    'Access to both employees and contractors in one ecosystem',
  ];

  const faqs = [
    { q: 'Is StaffDO live?', a: 'Yes—live and growing.' },
    { q: 'Do you offer verification?', a: 'Verification standards depend on role type; contractor verification can include portfolio-based review.' },
    { q: 'How do employers pay?', a: 'Pricing varies by plan and usage—request a demo for details.' },
  ];

  return (
    <div className="bg-white pt-32 pb-20">
      {/* Hero */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-serif text-5xl md:text-7xl font-bold text-stone-900 mb-6">
            StaffDO
          </h1>
          <p className="text-2xl text-stone-600 mb-6">
            Dominican Republic staffing marketplace—built for trust and speed.
          </p>
          <p className="text-lg text-stone-600 mb-10 leading-relaxed">
            A hiring platform connecting businesses with verified talent, now live.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button className="bg-stone-900 text-white hover:bg-stone-800 px-8 py-3">
              Join as an Employer
            </Button>
            <Button variant="outline" className="border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white px-8 py-3">
              Join as Talent
            </Button>
            <Button variant="outline" className="border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white px-8 py-3">
              Join as a Contractor
            </Button>
          </div>
        </div>
      </section>

      {/* What StaffDO Is */}
      <section className="section-padding px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-6">What StaffDO Is</h2>
          <p className="text-lg text-stone-600 leading-relaxed mb-12">
            StaffDO helps companies hire faster with more clarity—while giving talent a credible way to get discovered and hired.
          </p>
          
          {/* Platform Preview - Styled like real StaffDO */}
          <div className="rounded-lg overflow-hidden shadow-2xl border border-stone-200">
            <a href="https://staffdo.com" target="_blank" rel="noopener noreferrer" className="block">
              {/* Header bar like StaffDO */}
              <div className="bg-white py-4 px-6 flex items-center justify-between border-b">
                <span className="text-2xl font-bold text-purple-600">Staff<span className="text-stone-900">DO</span></span>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-stone-500">Buscar por título, Habilidad o Cargo</span>
                  <span className="bg-purple-600 text-white px-4 py-2 text-sm">Iniciar sesión</span>
                </div>
              </div>
              
              {/* Content area */}
              <div className="bg-stone-50 p-8">
                <h3 className="text-2xl font-bold text-stone-900 mb-6">Empleos</h3>
                
                {/* Job cards preview */}
                <div className="space-y-4">
                  <div className="bg-white p-6 rounded-lg border border-purple-200 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded">Immediate Hire</span>
                        <h4 className="font-semibold text-stone-900 mt-2">Modista</h4>
                        <p className="text-sm text-stone-500">Lausant Trajes de Alquiler</p>
                        <p className="text-xs text-stone-400 mt-1">Santo Domingo Este, Santo Domingo</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-stone-500">Full-Time</span>
                        <p className="text-sm font-semibold text-stone-700 mt-2">RD$ 18,000.00 Per Month</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg border border-stone-200 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-stone-900">Planchadora</h4>
                        <p className="text-sm text-stone-500">Lavandería Lausell</p>
                        <p className="text-xs text-stone-400 mt-1">Santo Domingo Este</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-stone-500">Full-Time</span>
                        <p className="text-sm font-semibold text-stone-700 mt-2">RD$ 18,000.00 Per Month</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </a>
            
            <div className="bg-stone-900 text-white py-4 px-6 flex items-center justify-between">
              <div>
                <span className="font-semibold">staffdo.com</span>
                <p className="text-sm text-stone-400">Plataforma de empleos en República Dominicana</p>
              </div>
              <a 
                href="https://staffdo.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-purple-600 text-white px-4 py-2 text-sm font-semibold hover:bg-purple-700 transition-colors"
              >
                Visitar Plataforma →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Choose Your Path */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-12 text-center">Choose Your Path</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {paths.map((path, idx) => (
              <Card key={idx} className="border-perla hover:shadow-lg transition-shadow">
                <CardContent className="p-8 text-center">
                  <div className="flex justify-center mb-6">{path.icon}</div>
                  <h3 className="font-serif text-2xl font-bold text-stone-900 mb-4">{path.title}</h3>
                  <p className="text-stone-600 mb-4">{path.desc}</p>
                  {path.details && <p className="text-sm text-stone-600 mb-6">{path.details}</p>}
                  <Button className="w-full bg-stone-900 text-white hover:bg-stone-800">
                    {path.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contractors Module */}
      <section className="section-padding px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-6">Contractors & Professional Services (Module)</h2>
          <p className="text-lg text-stone-600 mb-8">
            Built for: companies that need specialized help on-demand and professionals who want verified credibility.
          </p>
          <h3 className="font-serif text-2xl font-bold text-stone-900 mb-4">How it works</h3>
          <ol className="space-y-3 text-stone-600">
            <li className="flex items-start">
              <span className="font-bold text-terracota mr-3">1.</span>
              Create your contractor profile + services
            </li>
            <li className="flex items-start">
              <span className="font-bold text-terracota mr-3">2.</span>
              Verification (identity / background / portfolio where relevant)
            </li>
            <li className="flex items-start">
              <span className="font-bold text-terracota mr-3">3.</span>
              Get discovered, booked, and rated
            </li>
          </ol>
        </div>
      </section>

      {/* What It Solves */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-8">What It Solves</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {solves.map((item, idx) => (
              <div key={idx} className="flex items-start space-x-3">
                <span className="w-2 h-2 bg-terracota rounded-full mt-2 flex-shrink-0"></span>
                <p className="text-stone-600">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-12 text-center">FAQ</h2>
          <div className="space-y-8">
            {faqs.map((faq, idx) => (
              <div key={idx}>
                <h3 className="font-semibold text-lg text-stone-900 mb-2">{faq.q}</h3>
                <p className="text-stone-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="section-padding px-6 bg-stone-900 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6">
            Build your team with clarity—and hire with confidence.
          </h2>
          <p className="text-lg mb-8 opacity-90">Response within 24–48 hours</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button className="bg-white text-stone-900 hover:bg-stone-100 px-8 py-3">
              Join as an Employer
            </Button>
            <Button variant="outline" className="border-white text-white hover:bg-white hover:text-stone-900 px-8 py-3">
              Join as Talent
            </Button>
            <Button variant="outline" className="border-white text-white hover:bg-white hover:text-stone-900 px-8 py-3">
              Join as a Contractor
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StaffDO;